import { useState, useCallback, useMemo } from 'react';

const WELLS_KEY = 'wht_deactivated_wells';
const HAULS_KEY = 'wht_deactivated_hauls';

function loadSet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveSet(key, set) {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

// Stable unique key for a single haul event.
export function haulKey(deviceId, tank, haul) {
  return `${deviceId}|${tank}|${haul.drop_start}`;
}

/**
 * Persisted deactivation state for wells and individual haul events.
 * Deactivated items are treated as bad/incorrect readings and excluded
 * from analysis unless explicitly shown.
 */
export default function useDeactivation() {
  const [wells, setWells] = useState(() => loadSet(WELLS_KEY));
  const [hauls, setHauls] = useState(() => loadSet(HAULS_KEY));

  const toggleWell = useCallback((deviceId) => {
    setWells(prev => {
      const next = new Set(prev);
      const id = String(deviceId);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveSet(WELLS_KEY, next);
      return next;
    });
  }, []);

  const toggleHaul = useCallback((key) => {
    setHauls(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveSet(HAULS_KEY, next);
      return next;
    });
  }, []);

  const isWellOff = useCallback((deviceId) => wells.has(String(deviceId)), [wells]);
  const isHaulOff = useCallback((key) => hauls.has(key), [hauls]);

  const clearAll = useCallback(() => {
    setWells(new Set());
    setHauls(new Set());
    saveSet(WELLS_KEY, new Set());
    saveSet(HAULS_KEY, new Set());
  }, []);

  const counts = useMemo(() => ({
    wells: wells.size,
    hauls: hauls.size,
  }), [wells, hauls]);

  return {
    deactivatedWells: wells,
    deactivatedHauls: hauls,
    toggleWell,
    toggleHaul,
    isWellOff,
    isHaulOff,
    clearAll,
    counts,
  };
}
